import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  useSite,
  useUpcomingDates,
  useReleases,
  useMedia,
  useReferences,
  useSets,
  usePageMeta,
} from "@/lib/content";
import { fileUrl } from "@/lib/api";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import EventRow from "@/components/site/EventRow";
import { SoundCloudIcon } from "@/components/site/SocialIcons";

const TICKER = [
  "NOT JUST HARD",
  "NOT JUST INDUSTRIAL",
  "SEIMEN DEXTER",
  "165 BPM",
  "MANNHEIM / GERMANY",
  "PURE EKSTASE",
];

export default function Home() {
  usePageMeta(
    "SEIMEN DEXTER — Hard / Industrial Techno | Official Website",
    "Official website of SEIMEN DEXTER — Hard / Industrial Techno DJ & producer from Mannheim, Germany. 165 BPM. Dates, music, media and booking."
  );
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const { data: site } = useSite();
  const { data: dates } = useUpcomingDates(5);
  const { data: releases } = useReleases();
  const { data: media } = useMedia();
  const { data: refs } = useReferences();
  const { data: sets } = useSets();

  const outNow = (releases || []).filter((r) => r.effective_status !== "upcoming");
  const featuredRelease =
    (releases || []).find((r) => r.featured) || outNow[0] || (releases || [])[0] || null;
  const previewMedia = (media || []).filter((m) => m.type === "image").slice(0, 6);
  const bg = fileUrl(site?.hero_bg_url || "/assets/artist_live_02.webp");
  const hasDates = dates && dates.length > 0;

  return (
    <>
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="hero"
        className="relative h-[100svh] min-h-[640px] overflow-hidden flex flex-col justify-end cursor-crosshair"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <div className="absolute inset-x-0 top-0 h-full md:top-[-15%] md:h-[115%]">
            <img
              src={bg}
              alt="SEIMEN DEXTER live"
              fetchPriority="high"
              className="h-full w-full object-cover object-center"
              style={{ animation: "kenburns 22s ease-in-out infinite alternate" }}
              draggable="false"
            />
          </div>
        </motion.div>
        {/* slow warehouse light sweep */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <div
            className="absolute -inset-x-1/2 inset-y-0 opacity-[0.10]"
            style={{
              background: "radial-gradient(60% 50% at 50% 30%, #ffffff 0%, transparent 70%)",
              animation: "lightsweep 14s ease-in-out infinite alternate",
            }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-ink/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-transparent to-ink/40" />

        <motion.div
          style={{ opacity: fade }}
          className="relative z-10 px-5 md:px-10 pb-14 md:pb-24 grid gap-8 md:gap-6 md:grid-cols-12 md:items-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 md:order-2 md:col-span-5 flex justify-end md:justify-start"
          >
            <img
              src="/assets/original_logo.png"
              alt="SEIMEN DEXTER"
              data-testid="hero-logo"
              className="h-16 sm:h-20 md:h-40 lg:h-44 w-auto object-contain"
              draggable="false"
            />
          </motion.div>
          <div className="order-2 md:order-1 md:col-span-7">
            <h1 className="font-display leading-[0.92] text-[15vw] md:text-[7.2vw]">
              <MaskLines
                animate
                delay={0.5}
                lines={[
                  "NOT JUST HARD.",
                  "NOT JUST INDUSTRIAL.",
                  <>
                    THIS IS <span className="text-bloodbright">SEIMEN DEXTER.</span>
                  </>,
                ]}
              />
            </h1>
          </div>
          <div className="order-3 md:col-span-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.25 }}
              className="md:mt-4 flex flex-wrap items-center gap-x-5 gap-y-2"
            >
              <p className="text-[11px] md:text-xs tracking-[0.3em] text-white/80 font-semibold">
                HARD / INDUSTRIAL TECHNO
              </p>
              <span className="text-blood font-display text-xl leading-none">//</span>
              <p className="text-[11px] md:text-xs tracking-[0.3em] text-white/60">
                MANNHEIM / GERMANY
              </p>
              <span className="text-blood font-display text-xl leading-none">//</span>
              <p className="text-[11px] md:text-xs tracking-[0.3em] text-white/60">165 BPM</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="mt-9 md:mt-12 flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto"
            >
              <Link data-testid="hero-cta-dates" to="/dates" className="btn-primary w-full sm:w-auto">
                DATES
              </Link>
              <Link data-testid="hero-cta-listen" to="/music" className="btn-ghost w-full sm:w-auto">
                LISTEN
              </Link>
              <Link data-testid="hero-cta-booking" to="/booking" className="btn-ghost w-full sm:w-auto">
                BOOKING
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          style={{ opacity: fade }}
          className="absolute right-6 md:right-10 bottom-8 z-10 hidden sm:flex flex-col items-center gap-3"
          aria-hidden="true"
        >
          <span className="text-[9px] tracking-[0.4em] text-white/40 [writing-mode:vertical-lr]">SCROLL</span>
          <span className="block w-px h-12 bg-gradient-to-b from-white/50 to-transparent" />
        </motion.div>
      </section>

      {/* TICKER */}
      <Marquee duration={36} className="border-y border-line py-5 md:py-7 bg-coal">
        {TICKER.map((t, i) => (
          <span key={i} className="flex items-center">
            <span
              className={`font-display text-4xl md:text-6xl px-6 whitespace-nowrap ${
                i % 2 ? "text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.25)]" : "text-white"
              }`}
            >
              {t}
            </span>
            <span className="text-blood text-2xl">//</span>
          </span>
        ))}
      </Marquee>

      {/* ABOUT TEASER */}
      <section className="px-5 md:px-10 py-20 md:py-36 grid gap-12 md:grid-cols-12">
        <Reveal className="md:col-span-5" y={40}>
          <div className="overflow-hidden border border-line">
            <img
              src={fileUrl(site?.about_image_url || "/assets/artist_live_01.webp")}
              alt="SEIMEN DEXTER performing"
              loading="lazy"
              className="w-full aspect-[3/4] object-cover transition-transform duration-1000 hover:scale-[1.04]"
              draggable="false"
            />
          </div>
        </Reveal>
        <div className="md:col-span-7 flex flex-col justify-center">
          <Reveal>
            <p className="kicker mb-5 md:mb-6">ABOUT</p>
            <h2 className="font-display text-6xl md:text-7xl leading-[0.95]">
              THIS IS
              <br />
              SEIMEN DEXTER.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-white/70 leading-relaxed">{site?.bio_text}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 border-l-2 border-blood pl-6">
              <p className="font-display text-3xl md:text-4xl leading-tight">
                {site?.philosophy_line1}
                <br />
                {site?.philosophy_line2}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link data-testid="home-about-more" to="/about" className="mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.3em] text-white/60 hover:text-white transition-colors">
              MORE ABOUT <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NEXT DATES */}
      <section data-testid="home-dates" className="px-5 md:px-10 py-20 md:py-32 bg-coal border-y border-line">
        <Reveal>
          <div className="flex items-end justify-between mb-8 md:mb-14">
            <div>
              <p className="kicker mb-4">LIVE</p>
              <h2 className="font-display text-5xl md:text-7xl leading-none">
                {hasDates ? "NEXT DATES" : "LIVE ON STAGE"}
              </h2>
            </div>
            {hasDates && (
              <Link data-testid="home-dates-all" to="/dates" className="hidden md:inline-flex items-center gap-3 text-[11px] tracking-[0.3em] text-white/60 hover:text-white transition-colors">
                ALL DATES <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </Reveal>
        {hasDates ? (
          <div className="border-b border-line">
            {dates.map((ev) => (
              <EventRow key={ev.id} ev={ev} />
            ))}
          </div>
        ) : (
          <Reveal>
            <div className="border-t border-b border-line py-14 md:py-20">
              <p className="font-display text-5xl md:text-6xl leading-[0.95]">
                NO DATES ANNOUNCED YET.
              </p>
              <p className="kicker mt-4">NEW SHOWS ARE ADDED REGULARLY — BOOKING IS OPEN</p>
              <Link data-testid="home-dates-empty-booking" to="/booking" className="btn-ghost mt-8">
                BOOK SEIMEN DEXTER
              </Link>
            </div>
          </Reveal>
        )}
        {hasDates && (
          <Link data-testid="home-dates-all-mobile" to="/dates" className="md:hidden mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.3em] text-white/60">
            ALL DATES <ArrowUpRight className="h-4 w-4" />
          </Link>
        )}
      </section>

      {/* FEATURED RELEASE — only when real content exists */}
      {featuredRelease && (
        <section data-testid="home-release" className="px-5 md:px-10 py-20 md:py-36 grid gap-12 md:grid-cols-12 items-center">
          <Reveal className="md:col-span-5" y={40}>
            <Link to={`/music/${featuredRelease.slug}`} className="block overflow-hidden border border-line group">
              {featuredRelease.cover_url ? (
                <img
                  src={fileUrl(featuredRelease.cover_url)}
                  alt={`${featuredRelease.artist} — ${featuredRelease.title} cover artwork`}
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-1000 group-hover:scale-[1.05]"
                  draggable="false"
                />
              ) : (
                <div className="w-full aspect-square bg-panel flex items-center justify-center">
                  <span className="kicker">NO COVER</span>
                </div>
              )}
            </Link>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <p className="kicker mb-6">
                {featuredRelease.effective_status === "upcoming" ? "UPCOMING RELEASE" : "LATEST RELEASE"}
              </p>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">{featuredRelease.title}</h2>
              <p className="mt-4 text-white/60 text-sm md:text-base">
                {featuredRelease.artist}
                {featuredRelease.label ? ` — ${featuredRelease.label}` : ""}
                {featuredRelease.catalog_number ? ` — ${featuredRelease.catalog_number}` : ""}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 flex flex-wrap gap-3 md:gap-4">
                {(featuredRelease.spotify_url || featuredRelease.soundcloud_url || featuredRelease.other_url) && (
                  <a
                    data-testid="home-release-listen"
                    href={featuredRelease.spotify_url || featuredRelease.soundcloud_url || featuredRelease.other_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary"
                  >
                    LISTEN
                  </a>
                )}
                {featuredRelease.soundcloud_url && (
                  <a data-testid="home-release-stream" href={featuredRelease.soundcloud_url} target="_blank" rel="noreferrer" className="btn-ghost">
                    STREAM
                  </a>
                )}
                {(featuredRelease.beatport_url || featuredRelease.apple_url) && (
                  <a
                    data-testid="home-release-buy"
                    href={featuredRelease.beatport_url || featuredRelease.apple_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost"
                  >
                    BUY
                  </a>
                )}
                <Link data-testid="home-release-all" to="/music" className="btn-ghost">
                  ALL MUSIC
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* LATEST SET — FEATURED SOUNDCLOUD SET */}
      {(() => {
        const featured = (sets || []).find((s) => s.featured);
        if (!featured) return null;
        const scEmbedUrl = featured.url
          ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(featured.url.split("?")[0])}&color=%237a0000&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`
          : "";
        return (
          <section data-testid="home-latest-set" className="px-5 md:px-10 pb-20 md:pb-36 border-t border-line">
            <Reveal>
              <h2 className="font-display text-5xl md:text-7xl leading-none">LATEST SET</h2>
              <p className="kicker mt-3 mb-8 md:mb-10">LIVE FROM THE RAVE</p>
              <p data-testid="latest-set-title" className="font-display text-3xl md:text-5xl leading-[1.02] mb-3 break-words">
                {featured.title}
              </p>
              {featured.detail && (
                <p className="text-[11px] tracking-[0.2em] text-white/40 mb-5">{featured.detail}</p>
              )}
              <div data-testid="latest-set-card" className="w-full border border-line bg-coal">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-72 xl:w-80 shrink-0 bg-panel flex items-center justify-center lg:border-r border-line">
                    {featured.artwork_url ? (
                      <img
                        src={featured.artwork_url}
                        alt={`${featured.title} — SoundCloud artwork`}
                        loading="lazy"
                        className="w-full aspect-square object-contain"
                        draggable="false"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center">
                        <img
                          src="/assets/original_logo.png"
                          alt="SEIMEN DEXTER"
                          className="h-24 w-auto object-contain opacity-80"
                          draggable="false"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-6 md:p-8 lg:p-10 flex flex-col justify-center gap-6 md:gap-8">
                    <div>
                      <p className="flex items-center gap-3 text-[10px] tracking-[0.3em] text-white/50">
                        <SoundCloudIcon className="h-4 w-4 text-bloodbright" />
                        SOUNDCLOUD — OFFICIAL
                      </p>
                      <p className="font-display text-2xl md:text-3xl leading-tight mt-4 break-words">
                        {featured.title}
                      </p>
                      {featured.detail && (
                        <p className="text-[11px] tracking-[0.2em] text-white/40 mt-3">{featured.detail}</p>
                      )}
                    </div>
                    {scEmbedUrl && (
                      <iframe
                        data-testid="latest-set-player"
                        title={`${featured.title} — SoundCloud player`}
                        src={scEmbedUrl}
                        allow="autoplay"
                        loading="lazy"
                        className="w-full h-[166px] border border-line"
                      />
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          </section>
        );
      })()}

      {/* MEDIA PREVIEW */}
      {previewMedia.length > 0 && (
        <section data-testid="home-media" className="py-20 md:py-32 border-t border-line">
          <Reveal className="px-5 md:px-10">
            <div className="flex items-end justify-between mb-8 md:mb-14">
              <div>
                <p className="kicker mb-4">MEDIA</p>
                <h2 className="font-display text-5xl md:text-7xl leading-none">FROM THE FLOOR</h2>
              </div>
              <Link data-testid="home-media-all" to="/media" className="hidden md:inline-flex items-center gap-3 text-[11px] tracking-[0.3em] text-white/60 hover:text-white transition-colors">
                ALL MEDIA <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="px-5 md:px-10 grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            {previewMedia.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.05} className={i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : ""}>
                <Link to="/media" className="block overflow-hidden border border-line group h-full">
                  <img
                    src={fileUrl(m.thumbnail_url || m.url)}
                    alt={m.title || "SEIMEN DEXTER media"}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.05] ${
                      i === 0 ? "aspect-[2/1] md:aspect-auto md:h-full" : "aspect-square"
                    }`}
                    draggable="false"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* REFERENCES */}
      {refs && refs.length > 0 && (
        <section data-testid="home-references" className="py-16 md:py-28 border-t border-line bg-coal">
          <Reveal className="px-5 md:px-10">
            <p className="kicker mb-8">SELECTED APPEARANCES</p>
          </Reveal>
          <Marquee duration={50}>
            {refs.map((r, i) => (
              <span key={r.id || i} className="flex items-center">
                <span className="font-display text-3xl md:text-5xl text-white/70 px-8 whitespace-nowrap">{r.name}</span>
                <span className="text-blood text-xl">●</span>
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* BOOKING CTA */}
      <section data-testid="home-booking-cta" className="relative overflow-hidden border-t border-line">
        <div className="absolute inset-0">
          <img
            src={fileUrl(site?.booking_image_url || "/assets/artist_live_03.webp")}
            alt="SEIMEN DEXTER in front of a rave crowd"
            loading="lazy"
            className="h-full w-full object-cover opacity-35"
            draggable="false"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/75" />
        </div>
        <div className="relative z-10 px-5 md:px-10 py-24 md:py-44">
          <Reveal>
            <img
              src="/assets/original_logo.png"
              alt=""
              aria-hidden="true"
              className="h-14 md:h-20 w-auto mb-10 opacity-90 object-contain"
              draggable="false"
            />
            <p className="kicker mb-6">BOOKING</p>
            <h2 className="font-display text-6xl md:text-8xl leading-[0.92]">
              {site?.booking_headline || "BOOK SEIMEN DEXTER"}
            </h2>
            <p className="mt-5 text-[11px] md:text-xs tracking-[0.3em] text-white/60">
              {site?.booking_subline || "CLUBS · FESTIVALS · INTERNATIONAL BOOKINGS"}
            </p>
            {site?.booking_email && (
              <a
                data-testid="home-booking-email"
                href={`mailto:${site.booking_email}`}
                className="mt-8 inline-flex items-center gap-3 text-sm md:text-base text-white underline underline-offset-8 decoration-blood hover:text-bloodbright transition-colors break-all"
              >
                {site.booking_email}
              </a>
            )}
            <div>
              <Link data-testid="home-booking-button" to="/booking" className="btn-primary mt-10 w-full sm:w-auto">
                SEND BOOKING REQUEST
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
